import { useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';

const testimonials = [
  {
    quote: "After getting married, I had no idea whether filing jointly or separately was better for us. KGOB walked us through both scenarios with actual numbers. We saved over $4,000 the first year just by choosing the right filing status and claiming credits we didn't know existed.",
    attribution: "Married Couple, Austin TX",
    tag: "Individual Tax Planning"
  },
  {
    quote: "I've worked with multiple CPA firms over the years, but KGOB stands out for their professionalism, responsiveness, and depth of knowledge. Sara and her team helped me resolve a complex tax issue efficiently.",
    attribution: "Business Owner, Charlotte NC",
    tag: "Tax Resolution"
  },
  {
    quote: "KGOB doesn't just do my taxes — they actually help me understand my numbers and make better decisions. It's like having a part-time CFO without the full-time cost.",
    attribution: "Small Business Owner, Denver CO",
    tag: "Ongoing Advisory"
  },
  {
    quote: "Very professional, efficient, and personable. They took the stress out of taxes completely. As a single filer with investments and a side gig, I was always nervous about getting it right. Now I don't worry at all.",
    attribution: "Individual Client, Miami FL",
    tag: "Individual Tax Prep"
  }
];

const stats = [
  { label: "Years Experience", value: "35+" },
  { label: "Clients Nationwide", value: "Thousands" },
  { label: "Returns Filed", value: "Thousands" },
  { label: "IRS Representation", value: "Included" }
];

export const Testimonials = () => {
  const [active, setActive] = useState(0);

  const next = useCallback(() => {
    setActive((p) => (p + 1) % testimonials.length);
  }, []);

  const prev = () => setActive((p) => (p - 1 + testimonials.length) % testimonials.length);

  useEffect(() => {
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [next]);

  return (
    <section
      className="relative bg-[#F7F6F2] py-20 lg:py-28"
      data-testid="welcome-testimonials"
    >
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-14"
        >
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#0B2240]">
            Don't take our word for it.
          </h2>
        </motion.div>

        {/* Testimonial Card */}
        <div className="relative max-w-3xl mx-auto">
          <motion.div
            key={active}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.35 }}
            className="bg-white rounded-2xl p-8 sm:p-10 border border-gray-100 shadow-sm"
            data-testid="testimonial-card"
          >
            <Quote className="h-8 w-8 text-[#D4AF37]/30 mb-4" />

            <div className="flex gap-1 mb-5">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-4 w-4 fill-[#D4AF37] text-[#D4AF37]" />
              ))}
            </div>

            <p className="text-[#0B2240] text-lg sm:text-xl leading-relaxed mb-6 font-medium">
              "{testimonials[active].quote}"
            </p>

            <div className="flex items-center justify-between">
              <p className="text-sm text-[#4C5A70]">
                — {testimonials[active].attribution}
              </p>
              <span className="text-xs font-medium text-[#D4AF37] bg-[#D4AF37]/10 px-3 py-1 rounded-full">
                {testimonials[active].tag}
              </span>
            </div>
          </motion.div>

          {/* Nav arrows */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <button
              onClick={prev}
              className="p-2 rounded-full border border-[#0B2240]/15 text-[#4C5A70] hover:text-[#0B2240] hover:border-[#0B2240]/30 transition-colors"
              data-testid="testimonial-prev"
              aria-label="Previous testimonial"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <div className="flex gap-2">
              {testimonials.map((_, i) => (
                <button data-testid="testimonials-button-1"
                  key={i}
                  onClick={() => setActive(i)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    i === active ? 'w-6 bg-[#D4AF37]' : 'w-2 bg-[#0B2240]/20'
                  }`}
                  aria-label={`Go to testimonial ${i + 1}`}
                />
              ))}
            </div>
            <button
              onClick={next}
              className="p-2 rounded-full border border-[#0B2240]/15 text-[#4C5A70] hover:text-[#0B2240] hover:border-[#0B2240]/30 transition-colors"
              data-testid="testimonial-next"
              aria-label="Next testimonial"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Stats Bar */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6"
          data-testid="welcome-stats-bar"
        >
          {stats.map((stat) => (
            <div key={stat.label} className="text-center">
              <p className="text-2xl sm:text-3xl font-bold text-[#0B2240]">{stat.value}</p>
              <p className="text-sm text-[#4C5A70] mt-1">{stat.label}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
